<template>
  <q-page class="profile-page">
    <div class="profile-container">
      <!-- Profile Header Card -->
      <div class="profile-header-card">
        <!-- Background gradient -->
        <div class="profile-header-bg">
          <div class="profile-header-glow" />
        </div>

        <div class="profile-header-content">
          <!-- Avatar -->
          <div class="profile-avatar-wrapper">
            <div class="profile-avatar">
              {{ userInitial }}
            </div>
          </div>

          <!-- Name and info -->
          <div class="profile-info">
            <div>
              <h1 class="profile-name">
                {{ authStore.user?.firstName }} {{ authStore.user?.lastName }}
              </h1>
              <p class="profile-email">{{ authStore.user?.email }}</p>
              <div class="profile-badges">
                <span class="badge-gold">{{ roleLabel }}</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Edit Form Card -->
      <div class="profile-form-card">
        <h2 class="profile-form-title">Editar perfil</h2>

        <form @submit.prevent="handleSave" class="profile-form">
          <div class="profile-form-row">
            <q-input
              v-model="form.firstName"
              label="Nombre"
              dense
              outlined
              dark
              :rules="[val => !!val || 'Nombre es requerido']"
              class="profile-input"
            />
            <q-input
              v-model="form.lastName"
              label="Apellido"
              dense
              outlined
              dark
              :rules="[val => !!val || 'Apellido es requerido']"
              class="profile-input"
            />
          </div>

          <q-input
            v-model="form.displayName"
            label="Nombre para mostrar"
            dense
            outlined
            dark
            class="profile-input"
          />

          <q-input
            v-model="form.phone"
            label="Teléfono"
            dense
            outlined
            dark
            class="profile-input"
          />

          <q-input
            v-model="form.bio"
            label="Bio"
            type="textarea"
            :rows="3"
            dense
            outlined
            dark
            class="profile-input"
          />

          <div class="profile-form-actions">
            <q-btn
              type="submit"
              :loading="saving"
              no-caps
              unelevated
              class="profile-save-btn"
              label="Guardar cambios"
            />
            <transition name="fade">
              <span v-if="saved" class="profile-saved-msg">Guardado</span>
            </transition>
          </div>
        </form>
      </div>

      <!-- Sign out -->
      <div class="profile-signout">
        <a href="#" class="profile-signout-link" @click.prevent="handleLogout">
          Cerrar sesión
        </a>
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { useAuthStore } from 'stores/useAuthStore';

const router = useRouter();
const $q = useQuasar();
const authStore = useAuthStore();

// Form state
const form = ref({
  firstName: '',
  lastName: '',
  displayName: '',
  phone: '',
  bio: '',
});

const saving = ref(false);
const saved = ref(false);

// ROLE_LABELS
const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrador',
  alumno: 'Alumno',
};

// Computed
const userInitial = computed(() =>
  (authStore.user?.firstName ?? '').charAt(0).toUpperCase(),
);

const roleLabel = computed(() =>
  ROLE_LABELS[authStore.user?.role ?? ''] ?? authStore.user?.role ?? '',
);

// Initialize form from user data
onMounted(() => {
  if (authStore.user) {
    form.value = {
      firstName: authStore.user.firstName,
      lastName: authStore.user.lastName,
      displayName: authStore.user.displayName ?? '',
      phone: authStore.user.phone ?? '',
      bio: authStore.user.bio ?? '',
    };
  }
});

async function handleSave() {
  saving.value = true;
  try {
    await authStore.updateProfile(form.value);
    saved.value = true;
    setTimeout(() => {
      saved.value = false;
    }, 3000);
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
    $q.notify({ type: 'negative', message: msg || 'No se pudo guardar el perfil' });
  } finally {
    saving.value = false;
  }
}

async function handleLogout() {
  await authStore.logout();
  await router.push('/login');
}
</script>

<style lang="scss" scoped>
.profile-page {
  display: flex;
  justify-content: center;
  padding: 24px 16px;
}

.profile-container {
  max-width: 640px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

// --- Profile Header Card ---
.profile-header-card {
  position: relative;
  overflow: hidden;
  background: var(--oa-bg-2, var(--color-sandy-beige));
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--oa-border, rgba(13, 43, 69, 0.12)) 50%, transparent);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.profile-header-bg {
  height: 112px;
  position: relative;
  background: linear-gradient(135deg, var(--oa-bg-1, var(--color-warm-linen)) 0%, var(--oa-bg-2, var(--color-sandy-beige)) 100%);
}

.profile-header-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at top right, var(--oa-gold-full, var(--color-ocean-light)) 0%, transparent 60%);
  opacity: 0.12;
}

.profile-header-content {
  padding: 0 24px 24px;
}

// --- Avatar ---
.profile-avatar-wrapper {
  margin-top: -48px;
  margin-bottom: 16px;
}

.profile-avatar {
  width: 96px;
  height: 96px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.875rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--oa-gold-dim, rgba(26, 74, 107, 0.55)), var(--oa-gold-full, var(--color-ocean-light)));
  color: var(--oa-bg-0, var(--color-white-cap));
  border: 3px solid var(--oa-bg-2, var(--color-sandy-beige));
  box-shadow: var(--oa-glow-gold, 0 0 16px rgba(46, 127, 181, 0.15));
}

// --- Name / Info ---
.profile-info {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.profile-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--oa-text-primary, var(--color-deep-charcoal));
  margin: 0;
  line-height: 1.3;
}

.profile-email {
  font-size: 0.875rem;
  color: color-mix(in srgb, var(--oa-text-primary, var(--color-deep-charcoal)) 50%, transparent);
  margin: 2px 0 0;
}

.profile-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.badge-gold {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: linear-gradient(135deg, var(--oa-gold-dim, rgba(26, 74, 107, 0.55)), var(--oa-gold-full, var(--color-ocean-light)));
  color: var(--oa-bg-0, var(--color-white-cap));
}

.badge-outline {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid var(--oa-border, rgba(13, 43, 69, 0.12));
  color: var(--oa-text-secondary, var(--color-charcoal-mist));
  text-transform: capitalize;
}

// --- Edit Form Card ---
.profile-form-card {
  background: var(--oa-bg-2, var(--color-sandy-beige));
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--oa-border, rgba(13, 43, 69, 0.12)) 50%, transparent);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  padding: 24px;
}

.profile-form-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--oa-text-disabled, rgba(13, 43, 69, 0.12));
  margin: 0 0 20px;
}

.profile-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.profile-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.profile-input {
  :deep(.q-field__control) {
    border-radius: 12px;
  }
  :deep(.q-field__label) {
    color: var(--oa-text-secondary, var(--color-charcoal-mist));
  }
  :deep(.q-field__native) {
    color: var(--oa-text-primary, var(--color-deep-charcoal));
  }
}

.profile-form-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.profile-save-btn {
  background: var(--oa-gold-full, var(--color-ocean-light)) !important;
  color: var(--color-white-cap) !important;
  font-weight: 600;
  border-radius: 12px;
  padding: 8px 24px;
}

.profile-saved-msg {
  font-size: 0.875rem;
  color: var(--oa-success, var(--oa-success));
}

// --- Sign Out ---
.profile-signout {
  text-align: center;
  padding-bottom: 16px;
}

.profile-signout-link {
  font-size: 0.875rem;
  color: color-mix(in srgb, var(--oa-danger, var(--color-coral)) 70%, transparent);
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: var(--oa-danger, var(--color-coral));
  }
}

// --- Fade transition ---
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// --- Responsive ---
@media (max-width: 480px) {
  .profile-form-row {
    grid-template-columns: 1fr;
  }
}
</style>
